import {Component, Injectable} from '@angular/core';
import {Campus} from '../../model/Campus';
import {MatDialog} from '@angular/material';
import 'firebase/firestore';
import {Helper} from '../../model/Helper';
import {ActivatedRoute, Router} from '@angular/router';
import {DataLoaderService} from '../../services/data-loader/data-loader.service';
import {GlobalService} from '../../services/global/global.service';
import {
  UploadAssetBundlePayload,
  RemoveAssetBundlePayload,
} from '../../components/edit-campus-assetbundles/edit-campus-assetbundles.component';
import {YesNoDialogComponent} from '../../components/yes-no-dialog/yes-no-dialog.component';
import {RemoveCoverPayload, UploadCoverPayload} from '../../components/edit-campus-cover/edit-campus-cover.component';
import {DeleteCampusPayload} from '../../components/edit-campus-delete/edit-campus-delete.component';
import {SaveCampusPayload} from '../../components/edit-campus-information/edit-campus-information.component';

@Component({
  selector: 'app-edit-campus',
  templateUrl: 'edit-campus.component.html',
  styleUrls: ['edit-campus.component.css'],
})

@Injectable()
export class EditCampusComponent {
  // View
  public campus: Campus;
  private buildingID: string;
  private file: any;
  private id: string;

  private globalService: GlobalService;
  private Helper = Helper;

  public loading: boolean;

  constructor(
    private globServ: GlobalService,
    private route: ActivatedRoute,
    private dataLoader: DataLoaderService,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.buildingID = '';
    this.globalService = globServ;
    route.params.subscribe((p) => {
      this.id = p.id;
      this.fetchCampus();
    });

    this.globalService.GetReloadTrigger().subscribe(() => {
      this.fetchCampus();
    });
  }

  public async fetchCampus(): Promise<void> {
    this.loading = true;
    this.campus = new Campus({});

    try {
      this.campus = await this.dataLoader.FetchCampus(this.id);
      this.loading = false;
    } catch (err) {
      this.router.navigateByUrl('/');
      this.globalService.ShowMessage(err.message);
    }
  }

  public saveCampus(payload: SaveCampusPayload): void {
    const dialogRef = this.dialog.open(YesNoDialogComponent);
    dialogRef.componentInstance.setData(
      'Override campus',
      `Are you sure you want to override ${payload.campus.name} (${payload.campus.id}) forever?`,
      'Override',
      'Cancel');
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        this.loading = true;

        try {
          await this.dataLoader.SaveCampus(payload.campus.id, payload.campus);
          await this.fetchCampus();
        } catch (err) {
          this.globalService.ShowMessage(err.message);
        }

        this.loading = false;
      }
    });
  }

  public async uploadAssetBundle(payload: UploadAssetBundlePayload): Promise<void> {
    if (payload.assetBundle) {
      this.loading = true;

      try {
        await this.dataLoader.UploadAssetBundle(payload.campus.id, payload.platform, payload.assetBundle);
        await this.fetchCampus();
      } catch (err) {
        this.globalService.ShowMessage(err.message);
        this.loading = false;
      }
    } else {
      this.globalService.ShowMessage('No file selected');
      this.loading = false;
    }
  }

  public removeAssetBundle(payload: RemoveAssetBundlePayload): void {
    const dialogRef = this.dialog.open(YesNoDialogComponent);
    dialogRef.componentInstance.setData(
      'Remove AssetBundle',
      `Are you sure you want to remove the AssetBundle for ${Helper.PlatformNames[payload.platform]}?`,
      'Remove',
      'Cancel');
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        this.loading = true;

        try {
          await this.dataLoader.RemoveAssetBundle(payload.campus.id, payload.platform);
          this.fetchCampus();
        } catch (err) {
          this.globalService.ShowMessage(err.message);
          this.loading = false;
        }
      }
    });
  }

  public async uploadCover(payload: UploadCoverPayload): Promise<void> {
    if (payload.file) {
      this.loading = true;

      try {
        await this.dataLoader.UploadCover(payload.campus.id, payload.file);
        await this.fetchCampus();
      } catch (err) {
        this.globalService.ShowMessage(err.message);
      }

      this.loading = false;
    } else {
      this.globalService.ShowMessage('No image selected');
      this.loading = false;
    }
  }

  public removeCover(payload: RemoveCoverPayload): void {
    const dialogRef = this.dialog.open(YesNoDialogComponent);
    dialogRef.componentInstance.setData(
      'Remove cover',
      `Are you sure you want to remove the cover image?`,
      'Remove',
      'Cancel');
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        this.loading = true;

        try {
          await this.dataLoader.RemoveCover(payload.campus.id);
          await this.fetchCampus();
        } catch (err) {
          this.globalService.ShowMessage(err.message);
        }

        this.loading = false;
      }
    });
  }

  public deleteCampus(payload: DeleteCampusPayload): void {
    const dialogRef = this.dialog.open(YesNoDialogComponent);
    dialogRef.componentInstance.setData(
      'Delete campus',
      `Are you sure you want to delete ${payload.campus.name} (${payload.campus.id}) forever?`,
      'Delete',
      'Cancel');
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        this.loading = true;

        try {
          await this.dataLoader.RemoveCampus(payload.campus.id);
          this.router.navigateByUrl('/');
        } catch (err) {
          this.globalService.ShowMessage(err.message);
        }

        this.loading = false;
      }
    });
  }
}
